import { AsyncPipe } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTooltip } from '@angular/material/tooltip'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderDataViewInteractionMode,
  ElderQuantityPipe,
  ElderStopEventPropagationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarContentDirective,
  IDataContext,
  Sort,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import {
  IPackageDialogOptions,
  PackageDetailDialogComponent,
} from '../../../packages/package-detail-dialog/package-detail-dialog.component'
import { ProductSalesQuantityPipe } from '../../../../features/pipes/product-sales-quantity.pipe'
import { ProductSalesTotalWeightPipe } from '../../../../features/pipes/product-total-weight.pipe'
import { ProcessPlan } from '../../../process/process-plans/process-plan'
import { ProcessPlanService } from '../../../process/process-plans/process-plan.service'
import { WorkCenterService } from '../../../shopfloor/workcenters/work-center.service'
import { ControlTerminalDialogService } from '../../../terminals/dialog/control-terminal-dialog.service'
import { WorkBatch } from '../../batches/work-batch'
import { OrderConfirmationState } from '../../prod-orders/prod-order'
import { WorkItemFilterComponent } from '../../work-orders/work-item-filter-component/work-item-filter.component'
import { WorkClearance, WorkOrder } from '../../work-orders/work-order'
import { MaterialComponent } from '../bom/material-component'
import { ItemManufacturingStatusComponent } from '../item-manufacturing-status/item-manufacturing-status.component'
import {
  WorkItemPack,
  WorkItemPackagesComponent,
} from '../packaging/work-item-packages/work-item-packages.component'
import {
  ValidationState,
  WorkItem,
  WorkItemSegmentStateUtil,
  WorkItemState,
  WorkItemUtils,
} from '../work-item'
import { WorkItemFilter, WorkItemService } from '../work-item.service'

@Component({
  selector: 'app-work-item-table',
  templateUrl: './work-item-table.component.html',
  styleUrls: ['./work-item-table.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    WorkItemFilterComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatIcon,
    MatTooltip,
    ItemManufacturingStatusComponent,
    WorkItemPackagesComponent,
    MatIconButton,
    ElderStopEventPropagationDirective,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    AsyncPipe,
    ElderTranslatePipe,
    ProductSalesQuantityPipe,
    ProductSalesTotalWeightPipe,
    ElderQuantityPipe,
  ],
})
export class WorkItemTableComponent implements OnInit, OnDestroy, AfterViewInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly logger = LoggerFactory.getLogger(this.constructor.name)

  public readonly involvedProcessPlanIds$: BehaviorSubject<string[]> = new BehaviorSubject<
    string[]
  >([])
  public readonly involvedProcessPlans$: Observable<ProcessPlan[]>

  public readonly data: IDataContext<WorkItem>

  private readonly workOrderId$ = new BehaviorSubject<string | null>(null)
  readonly workOrder$ = new BehaviorSubject<WorkOrder | null>(null)

  @Input()
  public displayTerminalLink = false

  @Input()
  public allowEdit = false

  @Input()
  public selectEnabled = false

  @Input()
  public interactionMode: ElderDataViewInteractionMode = 'open'

  @Output()
  public readonly itemSelected = new EventEmitter<WorkItem>()

  @Output()
  public readonly itemDeleteRequested = new EventEmitter<WorkItem[]>()

  @Output()
  public readonly itemEditRequested = new EventEmitter<WorkItem>()

  @Output()
  public readonly itemNewRequested = new EventEmitter<void>()

  @Output()
  public readonly selectionChange = new EventEmitter<WorkItem[]>()

  public readonly unsubscribe$ = new Subject<void>()

  public readonly WorkItemUtils = WorkItemUtils

  @Input()
  public selectableEvaluatorFn: (w: WorkItem) => boolean = () => true

  private readonly dialog = inject(MatDialog)

  private readonly destroyRef = inject(DestroyRef)

  private changeDetectorRef = inject(ChangeDetectorRef)
  private workItemService = inject(WorkItemService)
  private processPlanService = inject(ProcessPlanService)
  private workCenterService = inject(WorkCenterService)
  private terminalDialogService = inject(ControlTerminalDialogService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.data = DataContextBuilder.start<WorkItem>().pageSize(100).build(this.workItemService)

    this.involvedProcessPlans$ = this.processPlanService.fetchByIds$(this.involvedProcessPlanIds$)
  }

  /***************************************************************************
   *                                                                         *
   * Lifecycle                                                               *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.data.start([new Sort('number', 'asc')], this.data.filter.filtersSnapshot)
  }

  public ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges()
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()

    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set workOrder(order: WorkOrder) {
    if (order) {
      this.workOrder$.next(order)
      this.workOrderId = order?.id
    }
  }

  public get workOrder(): WorkOrder | null {
    return this.workOrder$.getValue()
  }

  @Input()
  public set workOrderId(workOrderId: string) {
    this.unsubscribe$.next()
    this.workOrderId$.next(workOrderId)

    if (workOrderId) {
      this.data.filter.updateFilter(WorkItemFilter.workOrderId(workOrderId))

      this.workItemService
        .itemChanged(workOrderId)
        .pipe(takeUntilDestroyed(this.destroyRef)) // used outside injection context
        .subscribe({
          next: (_) => this.data.reload(),
          error: (err) => this.logger.error('Work-Order events failed ', err),
        })
    }
  }

  @Input()
  public set workBatch(batch: WorkBatch) {
    this.workOrderId = batch.workOrderId
    this.data.filter.updateFilter(WorkItemFilter.workBatchId(batch.id))
    this.data.reload()
  }

  @Input()
  public set involvedProcessPlanIds(ids: string[]) {
    this.involvedProcessPlanIds$.next(ids)
  }

  /***************************************************************************
   *                                                                         *
   * Public Api                                                              *
   *                                                                         *
   **************************************************************************/

  public selectItem(item: WorkItem): void {
    this.itemSelected.next(item)
  }

  public requestDeleteAll(items: WorkItem[]) {
    this.itemDeleteRequested.next(items)
  }

  public canModifyItem(item: WorkItem) {
    return this.allowEdit
  }

  public isProductionUnfinished(item: WorkItem): boolean {
    return item?.productionRoute?.sequence?.some((s) =>
      WorkItemSegmentStateUtil.getAllNonCompleted().includes(s.state)
    )
  }

  public determineOrderClearance(order: WorkOrder | null): WorkClearance {
    return order?.clearance === WorkClearance.RELEASABLE &&
      order?.orderConfirmationState === OrderConfirmationState.CONFIRMED
      ? WorkClearance.RELEASABLE
      : WorkClearance.HOLD
  }

  public canOpenTerminal(item: WorkItem, workOrder: WorkOrder | null): boolean {
    return (
      this.isScheduled(workOrder) &&
      this.isProductionUnfinished(item) &&
      this.determineOrderClearance(workOrder) !== WorkClearance.HOLD
    )
  }

  public openTerminal(item: WorkItem, workOrder: WorkOrder): void {
    const wcId = item?.productionRoute?.sequence?.find((s) =>
      WorkItemSegmentStateUtil.getAllNonCompleted().includes(s.state)
    )?.workCenterId
    if (wcId) {
      this.workCenterService.findById(wcId).subscribe({
        next: (wc) =>
          this.terminalDialogService.openTerminalForWorkCenterWithOrder(wc, workOrder, true),
        error: (err) => this.logger.error('Could not load terminal for item: ' + item, err),
      })
    }
  }

  public isProducedProductDifferent(item: WorkItem): boolean {
    return !MaterialComponent.equals(item.orderedProduct, item.producedProduct)
  }

  public getTerminalButtonDisabledMessage(item: WorkItem, workOrder: WorkOrder): string {
    if (workOrder.clearance === WorkClearance.HOLD) {
      return 'orders.work.uncleared'
    }

    if (workOrder.orderConfirmationState === OrderConfirmationState.OPEN) {
      return 'orders.prod.unconfirmed'
    }

    if (!this.isProductionUnfinished(item)) {
      return 'WorkItemState.' + item.processingState
    }

    if (!this.isScheduled(workOrder)) {
      return 'processing.missing-scheduling'
    }

    return ''
  }

  public showPlanValidationWarning(item: WorkItem): boolean {
    return (
      item?.scheduleValidation !== ValidationState.VALIDATED &&
      item?.processingState === WorkItemState.OPEN
    )
  }

  packageClick(pack: WorkItemPack, company: string): void {
    if (pack) {
      this.dialog.open(PackageDetailDialogComponent, {
        maxWidth: '95vw',
        maxHeight: '95vw',
        width: '95vw',
        height: '95vh',
        data: <IPackageDialogOptions>{
          pkgId: pack.packageId,
          company,
        },
      })
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private isScheduled(workOrder: WorkOrder): boolean {
    return !!workOrder.scheduleInterval
  }
}
